import React from "react";
import { Link } from "react-router-dom";
import PageShell from "../components/PageShell";
import ProductCard from "../components/ProductCard";
import { bestSellers, polos, roundNeckTees } from "../mock";
import { ArrowRight, CheckCircle2, Truck, Flag } from "lucide-react";

export default function TShirts() {
  const tees = bestSellers.filter((p) => p.category === "T-shirts");
  const longSleeves = bestSellers.filter((p) => p.category === "Long sleeve shirts");

  return (
    <PageShell kicker="T-Shirts" title="Round-neck tees, built for your group" subtitle="Six crew-neck classics from the brands print-shops trust — pick one, open the Design Studio and lay out your artwork in minutes.">
      {/* USP strip */}
      <section className="max-w-6xl mx-auto px-5 lg:px-8 pt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: CheckCircle2, title: "No minimum order", desc: "Order as few as 1 or as many as 10,000." },
          { icon: Truck, title: "Free shipping", desc: "On every group order — no fine print." },
          { icon: Flag, title: "Printed in USA", desc: "Pressed and quality-checked in-house." },
        ].map((f, i) => {
          const Icon = f.icon;
          return (
            <div key={i} className="bg-white rounded-2xl border border-[color:var(--dms-navy)]/10 p-5 flex items-start gap-4" data-testid={`tshirts-usp-${i}`}>
              <div className="w-10 h-10 rounded-full bg-[color:var(--dms-peach-light)]/60 flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-[color:var(--dms-navy)]" /></div>
              <div>
                <h3 className="font-serif text-lg text-[color:var(--dms-navy)]">{f.title}</h3>
                <p className="mt-1 text-sm text-[color:var(--dms-navy)]/65">{f.desc}</p>
              </div>
            </div>
          );
        })}
      </section>

      {/* Round-neck crew tees */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-16" data-testid="round-neck-tees-section">
        <div className="text-center">
          <p className="kicker">Round-neck crew tees</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">6 classic tees. One Design Studio.</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">The six round-neck t-shirts most groups order — from the heavyweight Hanes Beefy-T to the retail-fit Bella+Canvas. Tap any one to open the Design Studio.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {roundNeckTees.map((p) => (
            <ProductCard
              key={p.id}
              product={p}
              showPriceLabel
              linkTo={`/design/${p.slug}`}
              ctaLabel="Design in studio"
            />
          ))}
        </div>
      </section>

      {/* Polos collection */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20">
        <div className="text-center">
          <p className="kicker">Also in our collection</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Polo shirts</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <p className="mt-5 text-[color:var(--dms-navy)]/70 max-w-2xl mx-auto">Professional polos for businesses, churches, teams and events. Left-chest logo, back print or both.</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {polos.map((p) => <ProductCard key={p.id} product={p} showPriceLabel />)}
        </div>
        <div className="text-center mt-10">
          <Link to="/polos" className="btn-ghost-navy" data-testid="see-all-polos-link">See all polos <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </section>

      {/* Tees */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20">
        <div className="text-center">
          <p className="kicker">Best-sellers</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Ringspun cotton tees</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {tees.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      {/* Long sleeves */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pt-20">
        <div className="text-center">
          <p className="kicker">Long sleeves</p>
          <h2 className="font-serif text-3xl md:text-4xl text-[color:var(--dms-navy)] mt-4">Soft cotton long sleeves</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {longSleeves.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      </section>

      <section className="bg-[color:var(--dms-navy)] py-20 mt-20">
        <div className="max-w-3xl mx-auto text-center px-5">
          <p className="kicker">Can't find your verse?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-white mt-4">We'll design one just for you.</h2>
          <div className="w-14 h-[2px] bg-[color:var(--dms-peach)] mx-auto mt-5" />
          <div className="mt-8"><Link to="/custom-design" className="btn-peach" data-testid="start-custom-design-link">Start my custom design <ArrowRight className="w-4 h-4" /></Link></div>
        </div>
      </section>
    </PageShell>
  );
}
